import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

const ACCOUNT_OVERLAY_BACKDROP_CSS = `
@media screen and (max-width: 749px) {
  dialog::backdrop,
  [popover]::backdrop {
    background: rgb(3 3 3 / 22%);
    -webkit-backdrop-filter: brightness(0.78) blur(8px);
    backdrop-filter: brightness(0.78) blur(8px);
  }
}

@media screen and (min-width: 750px) {
  dialog::backdrop,
  [popover]::backdrop {
    background: transparent;
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
  }
}
`;

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  /** @type {HTMLElement | null} */
  #accountElement = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
    this.#bindAccountOverlay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#unbindAccountOverlay();
  }

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Account sheet overlay is mobile-only. Desktop uses a popover with no page dim.
   * Backdrop styles are injected into shadow DOM because light-DOM `::backdrop`
   * cannot reach it. Desktop positioning is configured by the component's CSS
   * variable in the header actions snippet.
   */
  #bindAccountOverlay() {
    const account = this.querySelector('shopify-account');
    if (!(account instanceof HTMLElement)) return;

    this.#accountElement = account;

    if (customElements.get('shopify-account')) {
      this.#injectAccountSheetStyles();
      return;
    }

    customElements.whenDefined('shopify-account').then(() => {
      if (this.#accountElement === account) this.#injectAccountSheetStyles();
    });
  }

  #unbindAccountOverlay() {
    this.#accountElement = null;
  }

  #injectAccountSheetStyles() {
    const root = this.#accountElement?.shadowRoot;
    if (!root) return;

    let style = root.getElementById('y2k-overlay-backdrop');

    if (!style) {
      style = document.createElement('style');
      style.id = 'y2k-overlay-backdrop';
      root.appendChild(style);
    }

    style.textContent = ACCOUNT_OVERLAY_BACKDROP_CSS;
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
